import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, DoCheck, AfterViewInit, ViewChildren, QueryList } from '@angular/core';
import DynamicFormFieldDTO from '../../dtos/DynamicFormFieldDTO';
import { Dropdown } from 'primeng/dropdown';
import { MultiSelect } from 'primeng/multiselect';

@Component({
  selector: 'app-multi-dropdown-select',
  templateUrl: './multi-dropdown-select.component.html',
  styleUrls: ['./multi-dropdown-select.component.sass']
})
export class MultiDropdownSelectComponent implements OnInit, OnChanges, DoCheck, AfterViewInit {
  @Input() field: DynamicFormFieldDTO;
  @Input() options: any;
  @Input() fieldValue: any[] = [];
  @Input() parentRecord: any;
  @Input() quantityFieldKey: string = 'bundleItemsQty'; // Default key for quantity field
  @Input() selectionMode: 'keepOpen' | 'default' = 'default'; // Control if panel stays open
  @Output() valueChange = new EventEmitter<any[]>();
  @Output() fieldTouched = new EventEmitter<void>();

  @ViewChildren('dropdown') dropdowns: QueryList<Dropdown>;
  @ViewChildren('multiSelect') multiSelects: QueryList<MultiSelect>;

  /**
   * Each row's ngModel uses a new array reference on every change ([...value]).
   * Without trackBy, *ngFor treats that as a new item and destroys the p-multiSelect — panel closes.
   */
  trackByRowIndex(index: number): number {
    return index;
  }

  dropdownValues: any[] = [];
  quantity: number = 0;
  private previousQuantity: number = 0;
  
  // Determine if we're in multiselect mode
  get isMultiselect(): boolean {
    return this.field?.inputMode === 'multiselectDropdown';
  }

  constructor() {}

  ngOnInit() {
    this.initializeDropdowns();
  }

  ngAfterViewInit() {
    if (this.selectionMode === 'keepOpen') {
      this.applyKeepOpenBehavior();
    }

    // Re-apply when new dropdowns are added (quantity changes)
    this.multiSelects?.changes.subscribe(() => {
      if (this.selectionMode === 'keepOpen') {
        setTimeout(() => this.applyKeepOpenBehavior(), 0);
      }
    });
  }

  private applyKeepOpenBehavior(): void {
    // Override hide() method for all multiselects to prevent closing
    this.multiSelects?.forEach(multiSelect => {
      if (!(multiSelect as any)._hideOverridden) {
        (multiSelect as any)._hideOverridden = true;
        multiSelect.hide = () => {
          // Do nothing - keep panel open
        };
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Check for changes in parentRecord that affect quantity
    if (changes['parentRecord'] && this.parentRecord) {
      const quantityKey = this.field?.quantityFieldKey || this.quantityFieldKey;
      const newQuantity = this.parentRecord[quantityKey] || 0;
      
      if (newQuantity !== this.quantity) {
        this.quantity = newQuantity;
        this.initializeDropdowns();
      }
    }
    
    // Check for direct changes to fieldValue - but only if it's not caused by our own emission
    if (changes['fieldValue'] && changes['fieldValue'].previousValue !== undefined) {
      const newFieldValue = changes['fieldValue'].currentValue;
      
      // Only update if the fieldValue actually changed from an external source
      // (not from our own emission)
      const currentValues = this.dropdownValues.slice(0, this.quantity);
      const hasExternalChange = !this.arraysEqual(newFieldValue, currentValues);
      
      if (hasExternalChange) {
        this.updateDropdownValues();
      }
    }
    
    // Initialize on first load
    if (!changes['parentRecord']?.previousValue && this.parentRecord) {
      this.initializeDropdowns();
    }
  }

  ngDoCheck() {
    // Check if quantity has changed by looking at the parent record
    const quantityKey = this.field?.quantityFieldKey || this.quantityFieldKey;
    const currentQuantity = this.parentRecord?.[quantityKey] || 0;
    
    if (currentQuantity !== this.previousQuantity) {
      this.previousQuantity = currentQuantity;
      this.quantity = currentQuantity;
      this.initializeDropdowns();
    }
  }

  private initializeDropdowns() {
    const quantityKey = this.field?.quantityFieldKey || this.quantityFieldKey;
    const newQuantity = this.parentRecord?.[quantityKey] || 0;
    const oldQuantity = this.quantity;
    this.quantity = newQuantity;
    this.previousQuantity = this.quantity; // Keep track for DoCheck
    
    // Preserve existing dropdown values when resizing
    const existingValues = [...(this.dropdownValues || [])];
    
    // Create new array with the new quantity
    // For multiselect, initialize with empty arrays; for single select, with null
    this.dropdownValues = new Array(newQuantity).fill(null).map(() => {
      // Create a new instance for each dropdown to avoid reference sharing
      return this.isMultiselect ? [] : null;
    });
    
    // Preserve existing values up to the new quantity
    for (let i = 0; i < Math.min(existingValues.length, newQuantity); i++) {
      if (existingValues[i] !== null && existingValues[i] !== undefined) {
        // For multiselect, create a new array to avoid reference issues
        this.dropdownValues[i] = this.isMultiselect && Array.isArray(existingValues[i]) 
          ? [...existingValues[i]] 
          : existingValues[i];
      }
    }
    
    // If we have fieldValue from the parent, use it as backup
    if (this.fieldValue && Array.isArray(this.fieldValue)) {
      for (let i = 0; i < Math.min(this.fieldValue.length, newQuantity); i++) {
        // Only fill if current position is empty
        const isEmpty = this.isMultiselect 
          ? (!this.dropdownValues[i] || this.dropdownValues[i].length === 0)
          : (this.dropdownValues[i] === null || this.dropdownValues[i] === undefined);
          
        if (isEmpty && this.fieldValue[i] !== null && this.fieldValue[i] !== undefined) {
          // For multiselect, create a new array to avoid reference issues
          this.dropdownValues[i] = this.isMultiselect && Array.isArray(this.fieldValue[i])
            ? [...this.fieldValue[i]]
            : this.fieldValue[i];
        }
      }
    }
    
    // Only emit if there's an actual change to prevent infinite loops
    // This should only happen when:
    // 1. Quantity changes (user changed bundleItemsQty)
    // 2. Initial load
    const hasQuantityChanged = oldQuantity !== newQuantity;
    const isInitialLoad = oldQuantity === 0 && newQuantity > 0;
    
    if (hasQuantityChanged || isInitialLoad) {
      const shouldMarkAsTouched = hasQuantityChanged && oldQuantity > 0;
      this.emitUpdatedArray(shouldMarkAsTouched);
    }
  }

  private updateDropdownValues() {
    if (this.fieldValue && Array.isArray(this.fieldValue)) {
      for (let i = 0; i < Math.min(this.fieldValue.length, this.dropdownValues.length); i++) {
        // For multiselect, create a new array to avoid reference issues
        if (this.isMultiselect && Array.isArray(this.fieldValue[i])) {
          this.dropdownValues[i] = [...this.fieldValue[i]];
        } else {
          this.dropdownValues[i] = this.fieldValue[i];
        }
      }
    }
  }

  onDropdownChange(index: number, value: any) {
    if (!this.dropdownValues) {
      this.dropdownValues = new Array(this.quantity).fill(null).map(() => {
        return this.isMultiselect ? [] : null;
      });
    }
    
    // For multiselect, ensure we're storing a copy of the array to avoid reference issues
    if (this.isMultiselect && Array.isArray(value)) {
      this.dropdownValues[index] = [...value];
    } else {
      this.dropdownValues[index] = value;
    }
    
    this.emitUpdatedArray(true); // Always mark as touched when user directly changes dropdown
  }

  private emitUpdatedArray(markAsTouched: boolean = false) {
    // Create array that maintains the order and structure
    // Include all values up to the current quantity, including null/undefined for proper indexing
    const outputArray = this.dropdownValues.slice(0, this.quantity).map(value => {
      // For multiselect, ensure we're emitting a copy of the array to avoid reference issues
      if (this.isMultiselect && Array.isArray(value)) {
        return [...value];
      }
      return value;
    });
    
    this.valueChange.emit(outputArray);
    
    if (markAsTouched) {
      this.markFieldAsTouched();
    }
  }

  private markFieldAsTouched() {
    if (this.field) {
      this.field.touched = true;
    }
    this.fieldTouched.emit();
  }

  getDropdownLabel(index: number): string {
    return `${this.field.spawnerLabel || 'Spawner'} #${index + 1} ${this.field.categoryLabel || 'Categories'}`;
  }

  getAvailableOptions(currentIndex: number): any[] {
    if (!this.options || !this.options[this.field.key]) {
      return [];
    }

    const allOptions = this.options[this.field.key];
    
    // If duplicates are allowed, return all options
    if (this.field.allowDuplicates) {
      return allOptions;
    }
    
    // For filtering duplicates, collect all selected values from other dropdowns
    const selectedValues: any[] = [];
    this.dropdownValues.forEach((val, idx) => {
      if (idx !== currentIndex && val !== null && val !== undefined) {
        if (this.isMultiselect && Array.isArray(val)) {
          // For multiselect, add all selected values in the array
          selectedValues.push(...val);
        } else {
          // For single select, add the single value
          selectedValues.push(val);
        }
      }
    });
    
    // Filter out already selected values
    return allOptions.filter((option: any) => {
      const optionValue = option[this.field.optionValue || '_id'];
      return !selectedValues.includes(optionValue);
    });
  }

  private arraysEqual(a: any[], b: any[]): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    
    for (let i = 0; i < a.length; i++) {
      if (this.isMultiselect) {
        // For multiselect, compare arrays
        if (!this.arrayValuesEqual(a[i], b[i])) return false;
      } else {
        // For single select, compare values directly
        if (a[i] !== b[i]) return false;
      }
    }
    return true;
  }
  
  private arrayValuesEqual(a: any[], b: any[]): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    
    // Sort arrays before comparing to handle different orders
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    
    for (let i = 0; i < sortedA.length; i++) {
      if (sortedA[i] !== sortedB[i]) return false;
    }
    return true;
  }

}